import { randomUUID } from 'node:crypto';
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result as MediaResult, ServiceError } from '@kit/shared/result';
import { createR2ServiceFromEnv, MediaWriteService } from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';
import type { UntypedTableClient } from '@kit/supabase/types';

type Result<T, E = Error> =
  | { success: true; data: T }
  | { success: false; error: E };

const CONTENT_TYPE_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
};

/**
 * Sniff the REAL image type from the leading bytes (magic numbers), so an
 * owner-uploaded blob is trusted by its content, not its client-declared
 * Content-Type (which is attacker-controlled). Returns the canonical mime or
 * null when the bytes are not a recognised raster image. SVG is intentionally
 * unrecognised (never returned) — it is a stored-XSS vector and stays rejected.
 */
export function sniffImageContentType(bytes: Buffer): string | null {
  if (bytes.length < 12) return null;
  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png';
  }
  // GIF: "GIF8"
  if (
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38
  ) {
    return 'image/gif';
  }
  const header = bytes.toString('ascii', 0, 12);
  // WEBP: "RIFF"<size>"WEBP"
  if (header.startsWith('RIFF') && header.slice(8, 12) === 'WEBP') {
    return 'image/webp';
  }
  // AVIF: box ....'ftyp' then brand 'avif'/'avis'/'mif1'
  if (
    bytes.toString('ascii', 4, 8) === 'ftyp' &&
    ['avif', 'avis', 'mif1'].includes(bytes.toString('ascii', 8, 12))
  ) {
    return 'image/avif';
  }
  return null;
}

/**
 * Ownership context for the public `document_metadata` cover row.
 * `document_metadata.owner_id` / `owner_type` are NOT NULL, so every ingest
 * must supply them (shop covers use `owner_type: 'organization'`,
 * `owner_id: <shop_id>`).
 */
interface ExternalImageOwner {
  ownerType: string;
  ownerId: string;
}

/**
 * Extract the first og:image / twitter:image URL from a product page's HTML.
 * Mirrors the regex in scripts/backfill-shop-covers-from-product-links.sh so
 * the runtime path and the backfill stay behaviourally identical.
 */
function extractOgImage(html: string): string | null {
  const tags = html.replace(/>/g, '>\n');
  for (const line of tags.split('\n')) {
    if (!/og:image|twitter:image/i.test(line)) continue;
    const m = line.match(/content="(https?:\/\/[^"]+)"/i);
    if (m?.[1]) return m[1];
  }
  return null;
}

/**
 * Host an external image DIRECTLY on R2 (cdn.ballee.co) — the durable,
 * CSP-safe form — and record it as a public `document_metadata` row.
 *
 * Shared tail of every external-cover ingest: download the bytes → upload to
 * R2 under `shop-covers/<uuid>.<ext>` → insert the public cover row whose
 * `external_url` points at the R2 public URL. Returns the new document id +
 * its public URL; the caller repoints whatever entity needs the cover.
 *
 * Consumed by:
 *   - `ingestShopArticleCover` (scrape-then-ingest, native shop covers)
 *   - the BAL-449 catalog sync (external brand-catalog product images)
 *
 * Result pattern — no exception escapes to the caller.
 */
export async function ingestExternalImage(
  client: SupabaseClient<Database>,
  imageUrl: string,
  owner: ExternalImageOwner,
): Promise<Result<{ documentId: string; publicUrl: string }>> {
  try {
    const db = client as unknown as UntypedTableClient;

    // 1. Download the image bytes.
    const imgRes = await fetch(imageUrl, {
      headers: { 'User-Agent': 'Mozilla/5.0 (BalleeMediaIngest)' },
      redirect: 'follow',
    });
    if (!imgRes.ok) {
      return {
        success: false,
        error: new Error(
          `Failed to download image (${imgRes.status}): ${imageUrl}`,
        ),
      };
    }
    const contentType = (imgRes.headers.get('content-type') ?? 'image/jpeg')
      .split(';')[0]!
      .trim()
      .toLowerCase();
    const ext = CONTENT_TYPE_EXT[contentType] ?? 'jpg';
    const bytes = Buffer.from(await imgRes.arrayBuffer());
    if (bytes.byteLength === 0) {
      return {
        success: false,
        error: new Error(`Downloaded image is empty: ${imageUrl}`),
      };
    }

    // 2. Upload to R2 under a stable key keyed by the new document id.
    const documentId = randomUUID();
    const key = `shop-covers/${documentId}.${ext}`;
    const r2 = createR2ServiceFromEnv();
    const upload = await r2.upload(key, bytes, { contentType });
    if (!upload.isSuccess) {
      return {
        success: false,
        error: new Error(`R2 upload failed: ${upload.getError().message}`),
      };
    }
    const publicUrl = r2.getPublicUrl(key);

    // 3. Create the public cover row pointing at R2 (never the seller).
    const { error: insertErr } = await db.from('document_metadata').insert({
      id: documentId,
      bucket: 'shop-og-external',
      owner_type: owner.ownerType,
      owner_id: owner.ownerId,
      external_url: publicUrl,
      is_public: true,
      mime_type: contentType,
      storage_path: null,
    });
    if (insertErr) {
      return {
        success: false,
        error: new Error(
          `document_metadata insert failed: ${insertErr.message}`,
        ),
      };
    }

    return { success: true, data: { documentId, publicUrl } };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err : new Error('Image ingest failed'),
    };
  }
}

/** Public bucket for owner-uploaded product images (migration 20260721000000). */
export const SHOP_PRODUCT_IMAGES_BUCKET = 'shop-product-images';

/**
 * Store an owner-UPLOADED product image (raw bytes from their device) in the
 * dedicated public `shop-product-images` Supabase bucket and record it as a
 * public `document_metadata` row so `/api/media/[id]` resolves it and a
 * `shop_articles.featured_image_id` renders on the storefront + feed.
 *
 * This is the direct-file sibling of `ingestExternalImage` (external URL → R2):
 * same "blob + public document_metadata row" shape, different backend (owner
 * uploads go to the owner-writable Supabase bucket, not R2). Kept in this same
 * service file rather than forked into a parallel one.
 *
 * OWNERSHIP / RLS: the row is written under the caller's RLS-enforced owner
 * client. The `document_metadata` INSERT policy requires
 * `uploaded_by = auth.uid()` AND `can_access_document_owner(owner_type, owner_id)`.
 * We use `owner_type='account'`, `owner_id=<shop.account_id>` — the 'account'
 * branch checks `accounts_memberships`, which every brand team-account owner has
 * (personal accounts, which lack a membership row, are out of scope for this
 * surface). Using `owner_type='organization', owner_id=shop_id` (as the admin
 * external-cover path does) would fail here because that branch matches
 * `organization_memberships.organization_id = owner_id`, and a shop id is not an
 * organization id — that path only works under the admin client.
 *
 * Result pattern — no exception escapes to the caller.
 */
export async function uploadShopProductImage(
  client: SupabaseClient<Database>,
  params: {
    shopId: string;
    accountId: string;
    uploadedBy: string;
    bytes: Buffer;
  },
): Promise<Result<{ documentId: string; publicPath: string }>> {
  try {
    const db = client as unknown as UntypedTableClient;

    if (params.bytes.byteLength === 0) {
      return { success: false, error: new Error('Uploaded image is empty') };
    }

    // Trust the BYTES, not the client-declared Content-Type. Sniff the real
    // image type from the magic numbers; reject anything that isn't a
    // recognised raster image (SVG/HTML/polyglots never sniff to an image).
    const contentType = sniffImageContentType(params.bytes);
    if (!contentType || !CONTENT_TYPE_EXT[contentType]) {
      return {
        success: false,
        error: new Error('shop:imageUnsupportedType'),
      };
    }

    const ext = CONTENT_TYPE_EXT[contentType];
    const documentId = randomUUID();
    // Object key is namespaced by account (owner), NOT cast to uuid anywhere in
    // the bucket policy — the chat-attachments landmine was a folder-uuid cast.
    const storagePath = `${params.accountId}/${documentId}.${ext}`;

    // Route the upload + owning-row insert through MediaWriteService so the
    // bytes↔row pair is atomic: uploadAndRecord uploads the blob, runs the
    // writeRow closure, and rolls the object back if the insert fails — no
    // orphan object (BAL-657, ballee-media/no-unrouted-media-write).
    const recordResult = await new MediaWriteService(
      client,
    ).uploadAndRecord<string>({
      bucket: SHOP_PRODUCT_IMAGES_BUCKET,
      path: storagePath,
      body: params.bytes,
      uploadOptions: { contentType, upsert: false },
      writeRow: async (verifiedPath) => {
        const { error: insertErr } = await db.from('document_metadata').insert({
          id: documentId,
          bucket: SHOP_PRODUCT_IMAGES_BUCKET,
          owner_type: 'account',
          owner_id: params.accountId,
          uploaded_by: params.uploadedBy,
          storage_path: verifiedPath,
          external_url: null,
          is_public: true,
          mime_type: contentType,
        });
        if (insertErr) {
          return MediaResult.fail(
            ServiceError.database(
              `document_metadata insert failed: ${insertErr.message}`,
              insertErr,
            ),
          );
        }
        return MediaResult.ok(documentId);
      },
    });

    if (recordResult.isFailure) {
      return {
        success: false,
        error: new Error(recordResult.error?.message ?? 'Upload failed'),
      };
    }

    return { success: true, data: { documentId, publicPath: storagePath } };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err : new Error('Product image upload failed'),
    };
  }
}

/**
 * Host a shop article's external cover DIRECTLY on R2 (cdn.ballee.co) — the
 * durable, CSP-safe form — with no intermediate seller-URL state.
 *
 * Replaces the manual two-step (backfill-shop-covers-from-product-links.sh
 * then rehost-shop-covers-to-r2.sh): scrape the product page's og:image,
 * upload the bytes to R2 under `shop-covers/<uuid>.<ext>`, create a public
 * `document_metadata` row whose `external_url` points at the R2 public URL,
 * and set the article's `featured_image_id` to it. See
 * WIP_durable_external_shop_covers_2026_06_30.
 *
 * Result pattern — no exception escapes to the caller.
 */
export async function ingestShopArticleCover(
  client: SupabaseClient<Database>,
  articleId: string,
): Promise<Result<{ documentId: string; publicUrl: string }>> {
  try {
    const db = client as unknown as UntypedTableClient;

    const { data: articleRow, error: articleErr } = await db
      .from('shop_articles')
      .select('id, shop_id, external_url')
      .eq('id', articleId)
      .single();

    const article = articleRow as {
      id: string;
      shop_id: string;
      external_url: string | null;
    } | null;

    if (articleErr || !article) {
      return {
        success: false,
        error: new Error(
          `Shop article ${articleId} not found: ${articleErr?.message ?? 'no row'}`,
        ),
      };
    }
    if (!article.external_url) {
      return {
        success: false,
        error: new Error(`Shop article ${articleId} has no external_url`),
      };
    }

    // 1. Scrape the product page's og:image.
    const pageRes = await fetch(article.external_url, {
      headers: { 'User-Agent': 'Mozilla/5.0 (BalleeMediaIngest)' },
      redirect: 'follow',
    });
    if (!pageRes.ok) {
      return {
        success: false,
        error: new Error(
          `Failed to fetch product page (${pageRes.status}): ${article.external_url}`,
        ),
      };
    }
    const imageUrl = extractOgImage(await pageRes.text());
    if (!imageUrl) {
      return {
        success: false,
        error: new Error(`No og:image found at ${article.external_url}`),
      };
    }

    // 2. Download → R2 → public cover row (shared tail).
    const ingest = await ingestExternalImage(client, imageUrl, {
      ownerType: 'organization',
      ownerId: article.shop_id,
    });
    if (!ingest.success) {
      return ingest;
    }
    const { documentId, publicUrl } = ingest.data;

    // 3. Repoint the article's featured image at the new cover.
    const { error: updateErr } = await db
      .from('shop_articles')
      .update({
        featured_image_id: documentId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', articleId);
    if (updateErr) {
      return {
        success: false,
        error: new Error(
          `shop_articles featured_image_id update failed: ${updateErr.message}`,
        ),
      };
    }

    return { success: true, data: { documentId, publicUrl } };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err : new Error('Cover ingest failed'),
    };
  }
}
